import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '@/core/api'
import { useQuery, useMutation, UseMutateAsyncFunction, useQueryClient } from '@tanstack/react-query'
import mixpanel from '@/utils/mixpanel'

interface ResetPasswordData {
  newPassword: string
  token: string
  username: string
}

interface IUserContext {
  user: Parse.User<Parse.Attributes> | null | any
  setUser: React.Dispatch<React.SetStateAction<Parse.User<Parse.Attributes> | null>>
  loading: boolean
  login: UseMutateAsyncFunction<Parse.User<Parse.Attributes>, unknown, { email: string; password: string }, unknown>
  signup: UseMutateAsyncFunction<Parse.User<Parse.Attributes>, unknown, { email: string; password: string }, unknown>
  sendResetPasswordEmail: UseMutateAsyncFunction<void, unknown, { email: string }, unknown>
  resetPassword: UseMutateAsyncFunction<void, unknown, ResetPasswordData, unknown>
  deleteAccount: UseMutateAsyncFunction<void, unknown, void, unknown>
  logout: () => Promise<void>
  checkExists: (email: string) => Promise<boolean>
  dismissTooltip: () => Promise<void>
  watchUnwatchEtf: (etfId: string, watch: boolean) => Promise<void>
  bookmarkUnbookmarkBlog: (documentId: string, bookmark: boolean) => Promise<void>
  removeAllBookmarks: (bookmarkedBlogIds: string[]) => Promise<void>
}
const UserContext = createContext<IUserContext | undefined>(undefined)

// Helper to get email from user object (Parse.User or plain object)
function getUserEmail(user: any) {
  if (!user) return undefined
  if (typeof user.get === 'function') return user.get('email')
  return user.email
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Parse.User<Parse.Attributes> | null>(null)
  const [isLoadingState, setIsLoadingState] = useState(true)
  const queryClient = useQueryClient()

  const {
    data: currentUser,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['user'],
    // wrap the api call and return null on failure to prevent no permission error from showing when user is not logged in
    queryFn: async () => {
      try {
        return await api.auth.getCurrentUser()
      } catch (error) {
        return null
      }
    },
    onSuccess: (data) => {
      setUser(data)
      setIsLoadingState(false)
    },
    onError: () => {
      setUser(null)
      setIsLoadingState(false)
    }
  })

  useEffect(() => {
    if (currentUser) {
      setUser(currentUser)
      // Mixpanel: Set user email property on load
      const email = getUserEmail(currentUser)
      if (email) {
        mixpanel.identify(currentUser.id || (currentUser as any).objectId)
        mixpanel.people.set({ $email: email })
      }
    }
  }, [currentUser])

  const loginMutation = useMutation({
    mutationFn: (values: { email: string; password: string }) => api.auth.login(values),
    onSuccess: (user) => {
      queryClient.invalidateQueries(['user'])
      // Mixpanel: Track login
      if (user) {
        const email = getUserEmail(user)
        mixpanel.track('User Login')
        mixpanel.identify(user.id || (user as any).objectId)
        if (email) mixpanel.people.set({ $email: email })
      }
    }
  })

  const signupMutation = useMutation({
    mutationFn: (values: { email: string; password: string }) => api.auth.signUp(values),
    onSuccess: (user, variables) => {
      // After sign up, invalidate user query
      queryClient.invalidateQueries(['user'])
      // login user
      loginMutation.mutateAsync({ email: variables.email, password: variables.password })
      // Mixpanel: Track sign up
      if (user) {
        const email = getUserEmail(user)
        mixpanel.track('User Sign Up')
        mixpanel.identify(user.id || (user as any).objectId)
        if (email) mixpanel.people.set({ $email: email })
      }
    }
  })

  const checkExists = async (email: string) => {
    return await api.auth.checkExists(email)
  }

  const deleteAccountMutation = useMutation({
    mutationFn: () => api.auth.deleteAccount(),
    onSuccess: () => {
      queryClient.invalidateQueries(['user'])
      // Mixpanel: Track account deletion
      mixpanel.track('User Account Deletion')
      mixpanel.people.delete_user()
      mixpanel.reset()
    }
  })

  const sendResetPasswordEmailMutation = useMutation({
    mutationFn: (values: { email: string }) => api.auth.sendResetPasswordEmail(values.email),
    onSuccess: () => {
      // handle reset password success
    }
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordData) => api.auth.resetPassword(data),
    onSuccess: (_, variables) => {
      // After password reset, invalidate user queryr
      queryClient.invalidateQueries(['user'])
      // login user
      loginMutation.mutateAsync({ email: variables.username, password: variables.newPassword })
    }
  })

  const logout = async () => {
    await api.auth.logout()
    setUser(null)
    // Mixpanel: Track logout
    mixpanel.track('User Log Out')
    mixpanel.reset()
  }

  const dismissTooltip = async () => {
    await api.auth.dismissTooltip()
  }

  const watchUnwatchEtf = async (etfId: string, watch: boolean) => {
    // Optimistically update the watchlist count in the user object
    if (user) {
      // Create a copy of the user object to modify
      const updatedUser = { ...user } as any

      // Update the watchlist count locally
      const currentCount = updatedUser.qtyEtfsOnWatchList || 0
      updatedUser.qtyEtfsOnWatchList = watch
        ? Math.max(1, currentCount + 1) // Add to watchlist (ensure at least 1)
        : Math.max(0, currentCount - 1) // Remove from watchlist (ensure not negative)

      // Update the user state immediately
      setUser(updatedUser)
    }

    // Make the API call
    await api.auth.watchUnwatchEtf(etfId, watch)

    // Invalidate all ETF-related queries to ensure cache is updated with new watchlist status
    queryClient.invalidateQueries({ queryKey: ['etfs'] })
    queryClient.invalidateQueries({ queryKey: ['etfs-watchlist'] })
    queryClient.invalidateQueries({ queryKey: ['etfs-infinite'] })
  }

  // Optimistic update for blog bookmarks
  const bookmarkUnbookmarkBlog = async (documentId: string, bookmark: boolean) => {
    // Optimistically update the bookmarked blogs in the user object
    if (user) {
      // Create a copy of the user object to modify
      const updatedUser = { ...user } as any

      // Update the bookmarked blogs array locally
      const currentBookmarks = updatedUser.bookmarkedBlogs || []

      if (bookmark) {
        // Add to bookmarks if not already present
        if (!currentBookmarks.includes(documentId)) {
          updatedUser.bookmarkedBlogs = [...currentBookmarks, documentId]
        }
      } else {
        // Remove from bookmarks
        updatedUser.bookmarkedBlogs = currentBookmarks.filter((id: string) => id !== documentId)
      }

      // Update the user state immediately
      setUser(updatedUser)
    }

    // Make the API call
    await api.auth.bookmarkUnbookmarkBlog(documentId, bookmark)
  }

  const removeAllBookmarks = async (bookmarkedBlogIds: string[]) => {
    // Remove all bookmarks by calling unbookmark for each one
    for (const documentId of bookmarkedBlogIds) {
      await api.auth.bookmarkUnbookmarkBlog(documentId, false)
    }
  }

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        loading: isLoading || isLoadingState,
        login: loginMutation.mutateAsync,
        signup: signupMutation.mutateAsync,
        sendResetPasswordEmail: sendResetPasswordEmailMutation.mutateAsync,
        resetPassword: resetPasswordMutation.mutateAsync,
        logout,
        checkExists,
        deleteAccount: deleteAccountMutation.mutateAsync,
        dismissTooltip,
        watchUnwatchEtf,
        bookmarkUnbookmarkBlog,
        removeAllBookmarks
      }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}
